import { useRef } from 'react'
import { useAuthContext } from '@contexts/authContext'
import useFileUpload, { IS_LOADING } from '@hooks/useFileUpload'
import useDialog from '@hooks/useDialog'
import axios from '@utils/webRequests'
import styled from '@emotion/styled'
import ImageCropperDialiog from '@forms/ImageCropperDialog'
import useSnackBarContext from '@contexts/snackBarContext'
import { MapView } from '@contexts/mapDataContext'
import ReplaceRemoveDialog from './ReplaceRemoveDialog'
import VisuallyHiddenInput from './VisuallyHiddenInput'
import { ButtonBase, } from '@mui/material'
import useLoading from '@hooks/useLoading'
import { fontSize, size } from '@styles/theme'
import { PiSpinnerBold } from 'react-icons/pi'
import ImagePreparationLoading from './ImagePreparationLoading'

const ContainerButton = styled(ButtonBase)`
  display: flex;
  align-items: center;

  border-radius: 100px;
  background-color: grey;
  overflow: hidden;
`

const ButtonImage = styled.div`
  width: 100%;
  height: 100%;
`

const ImageUploadButton = ({ existingUrl, btnSize='3rem', formatRequestData, formatRemovalParams, onRemovalSuccess, onUpdateSuccess, uniqueDialogType, useImageIcon=false, headerText, aspect, slots={ containerButton: {} } }) => {
  const [isImageCropperOpen, openImageCropper, closeImageCropper] = useDialog(`image_croper_dialog_${uniqueDialogType}`, 'image-cropper-open')
  const [isReplaceDialogOpen, openReplaceDialog, closeReplaceDialog] = useDialog(`replace_dialog_${uniqueDialogType}`, 'replace-dialog-open')
  const { isLoading: isLoadingRemoval, isLoadingDelayed: isLoadingRemovalDelayed, setIsLoading: setIsLoadingRemoval } = useLoading()
  const { handleInstantUpdate, state: authState } = useAuthContext()
  const { addSnackBar, snackError } = useSnackBarContext()
  const hiddenInput = useRef()
  const {
    file,
    handleFileUpload,
    resetFileState,
    fileUrl,
    isFileLoaded,
  } = useFileUpload()
  const defaultImg = useImageIcon
    ? 'https://mandrake-app.b-cdn.net/dev/application/icons/image-fill.svg'
    : 'https://mandrake-app.b-cdn.net/dev/application/icons/person.svg'

  const handleClick = () => {
    if (existingUrl) {
      openReplaceDialog()
    } else {
      hiddenInput.current.click()
    }
  }

  const handleReplaceRemoveSelection = (selection) => {
    if (selection === 'replace') {
      closeReplaceDialog()
      hiddenInput.current.click()
    } else {
      setIsLoadingRemoval(true)
      const removalParams = formatRemovalParams()
      axios.delete(`api/my/images/profile_removal`, removalParams)
        .then((res) => {
          addSnackBar({ message: 'Picture removed successfully' })
          onRemovalSuccess(res.data)
          closeReplaceDialog()
          setIsLoadingRemoval(false)
        })
        .catch((error) => {
          snackError({ fallbackMessage: 'Failed to remove picture.', error })
          closeReplaceDialog()
          setIsLoadingRemoval(false)
        })
    }
  }
  
  const handleUploadWrapper = (e) => {
    handleFileUpload(e)
      .then(() => openImageCropper())
      .catch((err) => {
        let error = err
        if (Error.isError(error)) error = err.toString()
        snackError({ error, fallbackMessage: 'Browser could not process image. Please try another.' })
      })
  }

  const onImageCropperClose = () => {
    closeImageCropper()
    resetFileState()
  }

  const uploadSelectedImages = (croppedFile) => {
    const data = formatRequestData(file, croppedFile)
    return axios.post(`api/my/images/uploads`, data)
      .then((res) => {
        if (!authState.user) return;
        const updatedUser = res.data
        // const updatedUser = newImages.reduce((userData, image) => {
        //   if (userData?.imageUrls && image?.role && image?.url) {
        //     userData.imageUrls[image.role] = image.url
        //   } else {
        //     console.error('Could not update user images on client')
        //   }
        //   return userData
        // }, { ...authState.user })
        handleInstantUpdate(updatedUser)
        MapView.updateUser(updatedUser?.nearbyUuid, updatedUser)
        if (typeof onUpdateSuccess === 'function') onUpdateSuccess(updatedUser)
        addSnackBar({ message: 'Image uploaded successfully.' })
        onImageCropperClose()
      })
      .catch((error) => {
        snackError({ fallbackMessage: 'Image Upload Failed', error, })
        onImageCropperClose()
      })
  }

  return (
    <>
      {isFileLoaded === IS_LOADING && <ImagePreparationLoading />}
      <ReplaceRemoveDialog
        isOpen={isReplaceDialogOpen}
        setClosed={closeReplaceDialog}
        afterSelect={handleReplaceRemoveSelection}
        isLoading={isLoadingRemoval}
        isLoadingDelayed={isLoadingRemovalDelayed}
        loadingText='Removing Picture'
      />
      <ImageCropperDialiog
        isOpen={isImageCropperOpen}
        setClosed={onImageCropperClose}
        fileUrl={fileUrl}
        uploadImage={uploadSelectedImages}
        headerText={headerText}
        aspect={aspect}
      />
      <ContainerButton
        onClick={handleClick}
        style={{
          height: btnSize,
          width: btnSize,
        }}
        {...slots.containerButton}>
        <VisuallyHiddenInput
          ref={hiddenInput}
          type='file'
          accept='image/*'
          onChange={handleUploadWrapper}
        />
        <ButtonImage
          style={{
            backgroundImage: `url(${existingUrl || defaultImg})`,
            backgroundSize: 'cover',
            backgroundRepeat: 'norepeat',
            backgroundPosition: 'center',
            height: !existingUrl && '65%',
            width: !existingUrl && '65%',
          }}
        />
      </ContainerButton>
    </>
  )
}

export default ImageUploadButton
